/* Today's clock words (pure, unit-tested in tests/unit/homeTime.test.mjs).
 *
 * The day sheet prints times the way an officer reads a desk diary: a
 * change made today shows its clock ("08:41"), one made yesterday says so
 * ("yesterday 18:02"), anything older carries its day ("29 Aug 18:02").
 * Dates are the officer's LOCAL day — the browser's clock, never UTC —
 * so "yesterday" is the officer's yesterday. */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n)
}

/** A date-only ISO string ("2026-09-03") as the local calendar day it
 *  names — parsed by parts so no timezone shifts it. */
function dayOf(iso: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (!m) return new Date(NaN)
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
}

/** "Thursday 3 Sep 2026" — the page's h1. */
export function dayHeading(iso: string): string {
  const d = dayOf(iso)
  if (Number.isNaN(d.getTime())) return iso
  return `${WEEKDAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`
}

/** "2 Sep" — a day without its year (column headings). */
export function shortDay(d: Date): string {
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`
}

/** "20:03" — the local wall clock of an instant. */
export function clock(d: Date): string {
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** Local calendar-day key, for same-day comparisons. */
function dayKey(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** When a change happened, relative to the sheet's day: "08:41" today,
 *  "yesterday 18:02", else "29 Aug 18:02". `todayIso` is the sheet's
 *  date ("2026-09-03"). */
export function whenLabel(atIso: string, todayIso: string): string {
  const at = new Date(atIso)
  if (Number.isNaN(at.getTime())) return ''
  const today = dayOf(todayIso)
  const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1)
  const key = dayKey(at)
  if (key === dayKey(today)) return clock(at)
  if (key === dayKey(yesterday)) return `yesterday ${clock(at)}`
  return `${shortDay(at)} ${clock(at)}`
}

/** The local day and clock of an instant, as the two datums the
 *  "Last sign-in «day», «time»" sentence takes. */
export function signInParts(iso: string): { day: string; time: string } {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return { day: iso, time: '' }
  return { day: dayKey(d), time: clock(d) }
}

/** The local day of an instant as a date-only ISO string — the `since`
 *  datum of "Nothing changed since «since»". */
export function localDayIso(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : dayKey(d)
}

/** A folded line's time range, both ends in the sheet's idiom: "12:21–12:28"
 *  when both fall on one local day, else each end in full
 *  ("yesterday 18:02 – 08:41"). `todayIso` is the sheet's date. */
export function rangeLabel(fromIso: string, toIso: string, todayIso: string): string {
  const from = new Date(fromIso)
  const to = new Date(toIso)
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return whenLabel(toIso, todayIso)
  if (dayKey(from) === dayKey(to)) return `${whenLabel(fromIso, todayIso)}–${clock(to)}`
  return `${whenLabel(fromIso, todayIso)} – ${whenLabel(toIso, todayIso)}`
}

/** How long something has waited, the sign-off block's mono datum:
 *  "12 min" under an hour, "2 h" under a day, else "3 d". */
export function waitedLabel(sinceIso: string, nowIso: string): string {
  const since = new Date(sinceIso).getTime()
  const now = new Date(nowIso).getTime()
  if (Number.isNaN(since) || Number.isNaN(now)) return ''
  const minutes = Math.max(0, Math.floor((now - since) / 60000))
  if (minutes < 60) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} h`
  return `${Math.floor(hours / 24)} d`
}
